import * as React from "react";

import ClassNames from "classnames";
import InputLabel from "../InputLabel";
import Typography from "../Typography";
import Styles from "./TextField.module.css";

import IconSearch from "../_assets/icon-search.svg";
import IconSuccess from "../_assets/icon-success-circle.svg";
import IconError from "../_assets/icon-warning-circle.svg";

export interface Props {
  id: string;
  label: string;
  autoComplete?: "on" | "off";
  autoFocus?: boolean;
  className?: string;
  compact?: boolean;
  disabled?: boolean;
  defaultValue?: string;
  errors?: string[];
  helperText?: string;
  hideLabel?: boolean;
  name?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  inputRef?: any;
  nativeRequired?: boolean;
  required?: boolean;
  type?: "text" | "date" | "email" | "password" | "number" | "search";
  value?: string;
  showSuccess?: boolean;
  showError?: boolean;
  leadingIcon?: JSX.Element;
  trailingIcon?: JSX.Element;
}

const TextField: React.FC<Props> = props => {
  const {
    id,
    label,
    autoComplete,
    autoFocus = false,
    className,
    compact = false,
    disabled,
    defaultValue,
    errors = [],
    helperText,
    hideLabel,
    name,
    onChange,
    placeholder,
    inputRef,
    required = false,
    nativeRequired = false,
    type = "text",
    value,
    showSuccess,
    showError,
    leadingIcon,
    trailingIcon
  } = props;

  const showErrorState = errors.length > 0 || showError;

  return (
    <div className={Styles.wrapper}>
      <InputLabel
        label={label}
        htmlFor={id}
        required={required}
        nativeRequired={nativeRequired}
        hideLabel={hideLabel}
      />
      {type === "search" && <IconSearch className={Styles.fieldLeading} />}
      {leadingIcon && (
        <span className={Styles.fieldLeading}>{leadingIcon}</span>
      )}
      <input
        id={id}
        type={type}
        className={ClassNames({
          [Styles.field]: true,
          [Styles.error]: showErrorState,
          [Styles.success]: showSuccess,
          [Styles.compact]: compact,
          [Styles.leading]: type === "search" || leadingIcon,
          [className]: className
        })}
        placeholder={placeholder ? placeholder : undefined}
        disabled={disabled}
        onChange={onChange}
        autoComplete={autoComplete ? autoComplete : undefined}
        autoFocus={autoFocus}
        required={nativeRequired}
        name={name}
        value={value}
        defaultValue={defaultValue}
        ref={inputRef}
      />
      {showSuccess && <IconSuccess className={Styles.trailingIcon} />}
      {showErrorState && <IconError className={Styles.trailingIcon} />}
      {trailingIcon && (
        <span className={Styles.trailingIcon}>{trailingIcon}</span>
      )}
      {helperText && (
        <Typography body="small" className={Styles.helper}>
          {helperText}
        </Typography>
      )}
      {showErrorState &&
        errors.map((error, i) => {
          return (
            <Typography
              body="small"
              className={ClassNames({
                [Styles.errorMessage]: true,
                [Styles.firstError]: errors.length !== 1 && i === 0
              })}
              key={`error-${id}-${i}`}
            >
              {error}
            </Typography>
          );
        })}
    </div>
  );
};

export default TextField;
